import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { CommonArea, CommonAreaBooking } from '../../services/commonAreaReservationService';
import {
  areaBookingMode,
  calendarMonthDays,
  dayStateFor,
  isSelectableDay,
  noSelectableDaysReason,
  DayState,
} from './reservaRules';

/**
 * The área's month, open on the screen — not behind a button.
 *
 * The apps put the calendar in front of the resident with the taken days already painted
 * (AreaComumScreen.kt / AreaComumView.swift, both commented "always visible, no toggle"), while the
 * portaria was typing dd/mm/aaaa blind and only learning a day was taken when the form refused it.
 *
 * Same colours as the apps on purpose — orange for a day that already holds a reservation, a dot
 * under the number for a day with bookings, muted grey for a day the área simply does not take —
 * so a resident at the counter sees what they see on their phone. The rules are the shared ones in
 * reservaRules, so a day this grid greys out is exactly a day validateReservation would reject.
 */

export interface ReservaCalendarProps {
  area: CommonArea | null;
  bookings: CommonAreaBooking[];
  blackoutDates: string[];
  /** yyyy-mm-dd, or '' for nothing picked yet. */
  value: string;
  onChange: (dateISO: string) => void;
  /** Hourly áreas: the length being booked — a day full for 3h can still be free for 1h. */
  durationHours?: number;
  /** Injected by tests so "hoje" is deterministic. */
  now?: Date;
  lightTheme?: boolean;
}

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
// Written out rather than toLocaleDateString: `capitalize` turns "outubro de 2026" into
// "Outubro De 2026", and only the month is a proper noun here.
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const STATE_TITLE: Record<DayState, string> = {
  free: 'Disponível',
  partial: 'Dia com reservas — ainda há horário livre',
  full: 'Dia indisponível (já reservado)',
  blackout: 'Data bloqueada para reservas',
  closed: 'Fechado neste dia da semana',
  past: 'Data passada',
  beyond: 'Fora do prazo de antecedência',
};

const cn = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

const localDateString = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const formatBR = (dateISO: string): string =>
  `${dateISO.slice(8, 10)}/${dateISO.slice(5, 7)}/${dateISO.slice(0, 4)}`;

const ReservaCalendar: React.FC<ReservaCalendarProps> = ({
  area,
  bookings,
  blackoutDates,
  value,
  onChange,
  durationHours = 1,
  now,
  lightTheme = true,
}) => {
  const today = useMemo(() => now ?? new Date(), [now]);
  const todayISO = localDateString(today);

  // Opens on the month of whatever is already picked, otherwise on this one.
  const [cursor, setCursor] = useState(() => {
    if (value) {
      const [y, m] = value.split('-').map(Number);
      if (y && m) return new Date(y, m - 1, 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const days = useMemo(() => calendarMonthDays(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const states = useMemo(() => {
    const map = new Map<string, DayState>();
    if (!area) return map;
    for (const day of days) {
      map.set(day.dateISO, dayStateFor({
        area,
        dateISO: day.dateISO,
        bookings,
        blackoutDates,
        now: today,
        durationHours,
      }));
    }
    return map;
  }, [area, days, bookings, blackoutDates, today, durationHours]);

  // Why the month takes no clicks, when it takes none — an empty grid under a legend promising
  // "Disponível" reads as a broken screen, and the commonest cause (no área chosen yet) is a
  // prerequisite the operator cannot guess.
  const nothingToPick = noSelectableDaysReason(area, Array.from(states.values()));

  // Nothing before this month can be booked, so there is nowhere to page back to.
  const atFirstMonth =
    cursor.getFullYear() === today.getFullYear() && cursor.getMonth() === today.getMonth();

  const hourly = !!area && areaBookingMode(area) === 'time_slot';
  const allowedDays = area?.available_days_of_week ?? [];
  const restrictedDays = allowedDays.length > 0 && allowedDays.length < 7;
  const advanceDays = area?.max_advance_days ?? 0;
  const lastBookableISO = advanceDays > 0
    ? localDateString(new Date(today.getFullYear(), today.getMonth(), today.getDate() + advanceDays))
    : '';

  const text = lightTheme ? 'text-gray-900' : 'text-gray-100';
  const subtext = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const muted = lightTheme ? 'text-gray-400' : 'text-gray-600';
  const shell = lightTheme ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-700';
  const navBtn = cn(
    'p-1 rounded-md disabled:opacity-30 disabled:cursor-not-allowed',
    lightTheme ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-300',
  );

  const cellClass = (state: DayState, selected: boolean): string => {
    if (selected) return 'bg-blue-600 text-white font-semibold';
    switch (state) {
      // Free days are painted, not merely left blank: at a counter the operator is looking for
      // where they CAN put the resident, and green is the only thing on the grid that says so.
      case 'free':
        return lightTheme
          ? 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
          : 'bg-emerald-900/25 text-emerald-300 hover:bg-emerald-900/40';
      // Still bookable, but the day already holds something — the dot under the number says so.
      // Not an amber fill under amber text: that pairing is unreadable on the white theme.
      case 'partial':
        return lightTheme
          ? 'bg-white text-gray-900 ring-1 ring-inset ring-gray-200 hover:bg-gray-50'
          : 'bg-gray-800/60 text-gray-100 hover:bg-gray-800';
      case 'full':
        return cn(
          'cursor-not-allowed',
          lightTheme ? 'bg-gray-100 text-gray-400' : 'bg-gray-800 text-gray-500',
        );
      // A day that has simply gone by reads as spent — struck through on its own grey — rather
      // than as a day the área happens to refuse.
      case 'past':
        return cn(
          'cursor-not-allowed line-through',
          lightTheme ? 'bg-gray-100 text-gray-400' : 'bg-gray-800 text-gray-500',
        );
      default:
        return cn('cursor-not-allowed', muted);
    }
  };

  const monthLabel = `${MONTHS[cursor.getMonth()]} de ${cursor.getFullYear()}`;

  return (
    <div className={cn('rounded-lg border p-3', shell)}>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          aria-label="Mês anterior"
          disabled={atFirstMonth}
          onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
          className={navBtn}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className={cn('text-sm font-semibold', text)}>{monthLabel}</span>
        <button
          type="button"
          aria-label="Próximo mês"
          onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
          className={navBtn}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEKDAYS.map((w, i) => (
          <div key={`${w}-${i}`} className={cn('text-center text-[11px] font-medium py-0.5', subtext)}>
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {days.map(day => {
          const state = states.get(day.dateISO) ?? 'closed';
          const selectable = !!area && isSelectableDay(state);
          const selected = value === day.dateISO;
          const dayNumber = Number(day.dateISO.slice(8, 10));
          // A dot means the day already holds a reservation — occupied days keep it even when they
          // are still bookable (an hourly área with hours left), exactly as the apps mark them.
          const hasBookings = state === 'partial' || state === 'full';
          return (
            <button
              key={day.dateISO}
              type="button"
              data-state={state}
              disabled={!selectable}
              onClick={() => onChange(day.dateISO)}
              title={area ? STATE_TITLE[state] : undefined}
              aria-label={area ? `${formatBR(day.dateISO)} — ${STATE_TITLE[state]}` : formatBR(day.dateISO)}
              aria-pressed={selected}
              className={cn(
                'relative h-9 rounded-md text-xs leading-none transition-colors',
                // Today keeps a ring, so the operator can place "hoje" at a glance.
                !selected && day.dateISO === todayISO && 'ring-1 ring-blue-400',
                !day.inMonth && 'opacity-40',
                cellClass(state, selected),
              )}
            >
              <span className="block pt-0.5">{dayNumber}</span>
              <span
                aria-hidden="true"
                className={cn(
                  'block h-1 w-1 rounded-full mx-auto mt-1',
                  hasBookings ? (selected ? 'bg-white' : 'bg-orange-500') : 'bg-transparent',
                )}
              />
            </button>
          );
        })}
      </div>

      {nothingToPick ? (
        <p className={cn('mt-2 text-[11px]', subtext)}>{nothingToPick}</p>
      ) : (
      <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px]', subtext)}>
        <span className="flex items-center gap-1">
          <span className={cn('h-2.5 w-2.5 rounded-sm', lightTheme ? 'bg-emerald-100' : 'bg-emerald-900/50')} />
          Disponível
        </span>
        {hourly && (
          <span className="flex items-center gap-1">
            {/* A dot, because that is what the cell itself now carries — the amber FILL it used
                to mean put amber text on an amber background. */}
            <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
            Com reservas
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className={cn('h-2.5 w-2.5 rounded-sm', lightTheme ? 'bg-gray-200' : 'bg-gray-700')} />
          {hourly ? 'Sem horário livre' : 'Dia indisponível (já reservado)'}
        </span>
        <span className="flex items-center gap-1">
          <span className={cn('h-2.5 w-2.5 rounded-sm', lightTheme ? 'bg-gray-100' : 'bg-gray-800')} />
          Data passada
        </span>
        {(restrictedDays || blackoutDates.length > 0) && (
          <span className="flex items-center gap-1">
            <span className={cn('h-1.5 w-1.5 rounded-full', lightTheme ? 'bg-gray-300' : 'bg-gray-600')} />
            Indisponível para reserva
          </span>
        )}
      </div>
      )}

      {area && (restrictedDays || advanceDays > 0) && (
        <div className={cn('mt-2 space-y-0.5 text-[11px]', subtext)}>
          {advanceDays > 0 && (
            <p>Reservas somente até {formatBR(lastBookableISO)} (máx. {advanceDays} dias de antecedência)</p>
          )}
          {restrictedDays && (
            <p>Dias disponíveis: {allowedDays.slice().sort().map(d => WEEKDAY_SHORT[d]).join(', ')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ReservaCalendar;
